// Story 10.3 — Helpers puros para sincronizar filtros ↔ URL search params.
// Aislado de TanStack Router para tests.

import type { ConversationFilters } from './query-keys';

const STATUS_VALUES = ['active', 'expired', 'completed'] as const;
const SINCE_VALUES = ['today', '7d', '30d'] as const;

export function isValidStatus(v: unknown): v is ConversationFilters['status'] {
  return typeof v === 'string' && (STATUS_VALUES as readonly string[]).includes(v);
}

export function isValidSince(v: unknown): boolean {
  if (typeof v !== 'string') return false;
  if ((SINCE_VALUES as readonly string[]).includes(v)) return true;
  return !Number.isNaN(new Date(v).getTime());
}

/** Lee el shape de search params (TanStack o URLSearchParams) y arma filtros. */
export function parseConversationFilters(input: Record<string, unknown>): ConversationFilters {
  const out: ConversationFilters = {};
  if (typeof input.since === 'string' && isValidSince(input.since)) out.since = input.since;
  if (typeof input.until === 'string' && isValidSince(input.until)) out.until = input.until;
  if (typeof input.flow === 'string' && input.flow.length > 0) out.flow = input.flow;
  if (isValidStatus(input.status)) out.status = input.status;
  if (typeof input.q === 'string' && input.q.length > 0) out.q = input.q;
  return out;
}

/** Convierte filtros a query string para el endpoint REST. */
export function toQueryString(
  filters: ConversationFilters & { limit?: number; offset?: number },
): string {
  const params = new URLSearchParams();
  if (filters.since) params.set('since', filters.since);
  if (filters.until) params.set('until', filters.until);
  if (filters.flow) params.set('flow', filters.flow);
  if (filters.status) params.set('status', filters.status);
  if (filters.q) params.set('q', filters.q);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  return params.toString();
}

/** Compara dos filter sets para invalidar query keys. */
export function filtersEqual(a: ConversationFilters, b: ConversationFilters): boolean {
  return (
    a.since === b.since &&
    a.until === b.until &&
    a.flow === b.flow &&
    a.status === b.status &&
    a.q === b.q
  );
}
